import api from './api';

// =====================================================
// ROADMAP MARKETPLACE TYPES
// =====================================================

export interface RoadmapTemplate {
  id: string;
  template_owner_id: string;
  name: string;
  description: string;
  industry: string;
  functional_area: string;
  transformation_type: string;
  complexity_level: number;
  estimated_duration_months: number;
  estimated_cost_range_min?: number;
  estimated_cost_range_max?: number;
  tags: string[];
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  created_at: string;
  updated_at: string;
  published_at?: string;
  download_count?: number;
  tenant_name?: string; // Add tenant name field
  owner_name?: string; // Add owner name field
  owner_email?: string; // Add owner email field
  marketplace_visibility?: 'DRAFT' | 'PRIVATE' | 'PUBLIC'; // Add marketplace visibility
}

export interface RoadmapInitiative {
  id: string;
  roadmap_template_id: string;
  name: string;
  description: string;
  objectives: string;
  expected_outcomes: string;
  complexity_level: number;
  estimated_duration_weeks: number;
  cost_drivers: Record<string, any>;
  cost_calculation_formula: string;
  status: 'ACTIVE' | 'INACTIVE';
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface RoadmapProject {
  id: string;
  roadmap_template_id: string;
  name: string;
  description: string;
  objectives: string[];
  expected_outcomes: string[];
  complexity_level: number;
  estimated_duration_months: number;
  start_month?: number;
  cost_drivers: Record<string, any>;
  cost_calculation_formula: string;
  status: 'PLANNING' | 'ACTIVE' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED';
  hierarchy_type: 'PROGRAM' | 'PROJECT' | 'SUB_PROJECT';
  parent_id?: string;
  dependencies?: string[]; // Project IDs this project depends on
  industry: string;
  functional_area: string;
  transformation_type: string;
  estimated_cost_range_min?: number;
  estimated_cost_range_max?: number;
  tags: string[];
  created_at: string;
  updated_at: string;
}

export interface TenantRoadmap {
  id: string;
  tenant_id: string;
  roadmap_template_id: string;
  created_by_user_id: string;
  name: string;
  description: string;
  custom_configuration: Record<string, any>;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
  transformation_count?: number;
  completed_transformations?: number;
  created_at: string;
  updated_at: string;
  // Additional properties for UI compatibility
  industry?: string;
  functional_area?: string;
  transformation_type?: string;
  complexity_level?: number;
  estimated_duration_months?: number;
  estimated_cost_range_min?: number;
  estimated_cost_range_max?: number;
  tags?: string[];
  published_at?: string;
  rating_stats?: RoadmapRating;
  tenant_name?: string; // Add tenant name field
  owner_name?: string; // Add owner name field
  owner_email?: string; // Add owner email field
  hierarchical_content?: any; // Canonical hierarchy payload for timeline extraction
}

export interface TenantRoadmapEditorData {
  roadmap: TenantRoadmap;
  hierarchical_content?: any;
}

export interface RoadmapRating {
  average_rating: number;
  total_ratings: number;
  rating_distribution: Record<number, number>;
}

export interface MarketplaceStats {
  total_published_roadmaps: number;
  total_downloads: number;
  roadmaps_by_industry: Record<string, number>;
  roadmaps_by_functional_area: Record<string, number>;
  average_rating: number;
}

export interface RoadmapSearchFilters {
  industry?: string;
  functional_area?: string;
  transformation_type?: string;
  complexity_level?: number;
  tags?: string[];
}

// =====================================================
// ROADMAP MARKETPLACE SERVICE
// =====================================================

export const roadmapMarketplaceService = {
  // =====================================================
  // MARKETPLACE OPERATIONS
  // =====================================================

  /**
   * Get published roadmaps from marketplace
   */
  async getPublishedRoadmaps(filters?: RoadmapSearchFilters): Promise<RoadmapTemplate[]> {
    const params = new URLSearchParams();
    
    if (filters?.industry) params.append('industry', filters.industry);
    if (filters?.functional_area) params.append('functional_area', filters.functional_area);
    if (filters?.transformation_type) params.append('transformation_type', filters.transformation_type);
    if (filters?.complexity_level) params.append('complexity_level', filters.complexity_level.toString());
    if (filters?.tags) params.append('tags', filters.tags.join(','));

    const response = await api.get(`/marketplace/roadmaps?${params.toString()}`);
    return response.data.data || [];
  },

  /**
   * Get roadmap template details
   */
  async getRoadmapTemplate(id: string): Promise<RoadmapTemplate & { rating_stats: RoadmapRating }> {
    const response = await api.get(`/templates/${id}`);
    const template = response.data.data;
    
    // Transform template data to roadmap format
    const roadmapData: RoadmapTemplate & { rating_stats: RoadmapRating, hierarchical_content?: any } = {
      id: template.id,
      template_owner_id: template.created_by,
      name: template.name,
      description: template.description || '',
      industry: template.content?.template_metadata?.industry || 'General',
      functional_area: template.content?.template_metadata?.functional_area || 'General',
      transformation_type: template.template_type,
      complexity_level: template.content?.template_metadata?.complexity_level || 1,
      estimated_duration_months: template.content?.template_metadata?.estimated_duration_months || 12,
      estimated_cost_range_min: template.content?.template_metadata?.estimated_cost_range_min,
      estimated_cost_range_max: template.content?.template_metadata?.estimated_cost_range_max,
      tags: template.content?.template_metadata?.tags || [],
      status: template.is_published ? 'PUBLISHED' : 'DRAFT',
      created_at: template.created_at,
      updated_at: template.updated_at,
      published_at: template.is_published ? template.updated_at : undefined,
      download_count: 0,
      tenant_name: template.tenant_name,
      owner_name: template.owner_name,
      owner_email: template.owner_email,
      marketplace_visibility: template.marketplace_visibility || 'DRAFT', // Include marketplace visibility
      hierarchical_content: template.content, // Add the hierarchical content
      rating_stats: {
        average_rating: 0,
        total_ratings: 0,
        rating_distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      }
    };
    
    return roadmapData;
  },

  /**
   * Get roadmap initiatives
   */
  async getRoadmapInitiatives(roadmapId: string): Promise<RoadmapInitiative[]> {
    const response = await api.get(`/roadmap/templates/${roadmapId}/initiatives`);
    return response.data.data || [];
  },

  /**
   * Get project templates for a roadmap
   */
  async getRoadmapProjectTemplates(roadmapId: string): Promise<RoadmapProject[]> {
    const response = await api.get(`/templates/${roadmapId}`);
    const template = response.data.data;
    
    // Use shared extraction utility to ensure consistent hierarchy extraction
    const { extractProjectsFromHierarchy } = await import('@/utils/roadmapProjectExtractor');
    return extractProjectsFromHierarchy(template);
  },

  /**
   * Get tenant roadmap by ID
   */
  async getTenantRoadmap(roadmapId: string): Promise<TenantRoadmap> {
    const response = await api.get(`/tenant/roadmaps/${roadmapId}`);
    return response.data.data;
  },

  /**
   * Get tenant roadmap editor data (canonical hierarchical source)
   */
  async getTenantRoadmapEditorData(roadmapId: string): Promise<TenantRoadmapEditorData> {
    const response = await api.get(`/tenant/roadmaps/${roadmapId}/editor-data`);
    return response.data.data;
  },

  /**
   * Get tenant transformations for a roadmap
   */
  async getTenantTransformations(roadmapId: string): Promise<RoadmapProject[]> {
    const response = await api.get(`/tenant/roadmaps/${roadmapId}/projects`);
    return response.data.data || [];
  },

  /**
   * Get initiative projects
   */
  async getInitiativeProjects(initiativeId: string): Promise<RoadmapProject[]> {
    const response = await api.get(`/roadmap/initiatives/${initiativeId}/projects`);
    return response.data.data || [];
  },

  /**
   * Rate a roadmap
   */
  async rateRoadmap(
    roadmapId: string, 
    rating: number, 
    comments?: string
  ): Promise<void> {
    await api.post(`/marketplace/roadmaps/${roadmapId}/rate`, {
      rating,
      comments
    });
  },

  /**
   * Get marketplace statistics
   */
  async getMarketplaceStats(): Promise<MarketplaceStats> {
    const response = await api.get('/marketplace/roadmaps/stats');
    return response.data.data;
  },

  // =====================================================
  // TENANT OPERATIONS
  // =====================================================

  /**
   * Download roadmap to tenant
   */
  async downloadRoadmap(roadmapId: string, customName?: string): Promise<TenantRoadmap> {
    const response = await api.post(`/marketplace/roadmaps/${roadmapId}/download`, {
      custom_name: customName
    });
    return response.data.data;
  },

  /**
   * Get tenant roadmaps
   */
  async getTenantRoadmaps(): Promise<TenantRoadmap[]> {
    const response = await api.get('/tenant/roadmaps');
    return response.data.data || [];
  },

  // =====================================================
  // ROADMAP CREATION
  // =====================================================

  /**
   * Create roadmap template
   */
  async createRoadmapTemplate(templateData: Omit<RoadmapTemplate, 'id' | 'created_at' | 'updated_at' | 'published_at'>): Promise<RoadmapTemplate> {
    const response = await api.post('/marketplace/roadmaps/', templateData);
    return response.data.data;
  },

  /**
   * Publish roadmap template
   */
  async publishRoadmapTemplate(roadmapId: string): Promise<RoadmapTemplate> {
    const response = await api.post(`/marketplace/roadmaps/${roadmapId}/publish`);
    return response.data.data;
  },

  /**
   * Get my roadmap templates (for template creators)
   */
  async getMyTemplates(filters?: RoadmapSearchFilters): Promise<RoadmapTemplate[]> {
        try {
            const params = new URLSearchParams();
      
      if (filters) {
        if (filters.industry) params.append('industry', filters.industry);
        if (filters.functional_area) params.append('functional_area', filters.functional_area);
        if (filters.transformation_type) params.append('transformation_type', filters.transformation_type);
        if (filters.complexity_level) params.append('complexity_level', filters.complexity_level.toString());
        if (filters.tags && filters.tags.length > 0) params.append('tags', filters.tags.join(','));
      }
      
      const queryString = params.toString();
      const url = queryString ? `/marketplace/roadmaps/my-templates?${queryString}` : '/marketplace/roadmaps/my-templates';
      
      const response = await api.get(url);
            return response.data.data;
    } catch (error) {
      console.error('🔍 [FRONTEND DEBUG] Error fetching my templates:', error);
      throw error;
    }
  },

  /**
   * Publish a roadmap template (DRAFT -> PUBLISHED)
   */
  async publishTemplate(templateId: string): Promise<void> {
    try {
      await api.patch(`/marketplace/roadmaps/${templateId}/publish`);
    } catch (error) {
      console.error('Error publishing template:', error);
      throw error;
    }
  },

  /**
   * Unpublish a roadmap template (PUBLISHED -> DRAFT)
   */
  async unpublishTemplate(templateId: string): Promise<void> {
    try {
      await api.patch(`/marketplace/roadmaps/${templateId}/unpublish`);
    } catch (error) {
      console.error('Error unpublishing template:', error);
      throw error;
    }
  },

  /**
   * Archive a roadmap template (any status -> ARCHIVED)
   */
  async archiveTemplate(templateId: string): Promise<void> {
    try {
      await api.patch(`/marketplace/roadmaps/${templateId}/archive`);
    } catch (error) {
      console.error('Error archiving template:', error);
      throw error;
    }
  },

  /**
   * Update a roadmap template
   */
  async updateTemplate(templateId: string, updates: Partial<RoadmapTemplate>): Promise<RoadmapTemplate> {
    try {
      const response = await api.put(`/marketplace/roadmaps/${templateId}`, updates);
      return response.data.data;
    } catch (error) {
      console.error('Error updating template:', error);
      throw error;
    }
  },

  /**
   * Delete a roadmap template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      await api.delete(`/marketplace/roadmaps/${templateId}`);
    } catch (error) {
      console.error('Error deleting template:', error);
      throw error;
    }
  },

  // =====================================================
  // UTILITY METHODS
  // =====================================================

  /**
   * Get industry options for filtering
   */
  async getIndustryOptions(): Promise<{ value: string; label: string }[]> {
    const roadmaps = await this.getPublishedRoadmaps();
    const industries = Array.from(new Set(roadmaps.map(r => r.industry)));
    return industries.map((industry: string) => ({ value: industry, label: industry }));
  },

  /**
   * Get functional area options for filtering
   */
  async getFunctionalAreaOptions(): Promise<{ value: string; label: string }[]> {
    const roadmaps = await this.getPublishedRoadmaps();
    const areas = Array.from(new Set(roadmaps.map(r => r.functional_area)));
    return areas.map(area => ({ value: area, label: area }));
  },

  /**
   * Get transformation type options for filtering
   */
  async getTransformationTypeOptions(): Promise<{ value: string; label: string }[]> {
    const roadmaps = await this.getPublishedRoadmaps();
    const types = Array.from(new Set(roadmaps.map(r => r.transformation_type)));
    return types.map(type => ({ value: type, label: type }));
  },

  /**
   * Get complexity level options
   */
  getComplexityLevelOptions(): { value: number; label: string }[] {
    return [
      { value: 1, label: 'Level 1 - Basic' },
      { value: 2, label: 'Level 2 - Simple' },
      { value: 3, label: 'Level 3 - Moderate' },
      { value: 4, label: 'Level 4 - Complex' },
      { value: 5, label: 'Level 5 - Advanced' }
    ];
  },

  // =====================================================
  // ROADMAP IMPLEMENTATION METHODS
  // =====================================================

  /**
   * Get roadmap projects (for tenant roadmaps)
   */
  async getRoadmapProjects(roadmapId: string): Promise<any[]> {
    try {
      const response = await api.get(`/tenant/roadmaps/${roadmapId}/projects`);
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to get roadmap projects:', error);
      return [];
    }
  },

  /**
   * Update initiative progress
   */
  async updateInitiativeProgress(initiativeId: string, progressData: any): Promise<void> {
    try {
      await api.put(`/marketplace/roadmaps/initiatives/${initiativeId}/progress`, progressData);
    } catch (error) {
      console.error('Failed to update initiative progress:', error);
      throw error;
    }
  },

  /**
   * Update project progress
   */
  async updateProjectProgress(projectId: string, progressData: any): Promise<void> {
    try {
      await api.put(`/marketplace/roadmaps/projects/${projectId}/progress`, progressData);
    } catch (error) {
      console.error('Failed to update project progress:', error);
      throw error;
    }
  },

  /**
   * Submit progress report
   */
  async submitProgressReport(roadmapId: string, report: string): Promise<void> {
    try {
      await api.post(`/marketplace/roadmaps/tenant/${roadmapId}/progress-report`, { report });
    } catch (error) {
      console.error('Failed to submit progress report:', error);
      throw error;
    }
  },

  /**
   * Update project timeline (duration and start date)
   */
  async updateProjectTimeline(roadmapTemplateId: string, projectId: string, updates: { estimated_duration_months?: number; start_month?: number }): Promise<RoadmapProject> {
    try {
      const response = await api.put(`/marketplace/roadmaps/templates/${roadmapTemplateId}/projects/${projectId}/timeline`, updates);
      return response.data.data;
    } catch (error) {
      console.error('Failed to update project timeline:', error);
      throw error;
    }
  }
};
